/* Render the existing Shopify review section near the reader, preserving app data and form. */
(() => {
  const host = document.querySelector('[data-ss-deferred-reviews]');
  if (!host) return;
  const link = host.querySelector('[data-ss-load-reviews]');
  const status = host.querySelector('[data-ss-reviews-status]');
  if (!link || !status || host.dataset.ssReviewsInitialized) return;
  host.dataset.ssReviewsInitialized = 'true';
  let pending = false;
  let ready = false;
  let needsReload = false;
  let wantsScroll = false;
  let reviewRoot = host;
  const reviewHash = hash => ['#customer-reviews', '#judgeme_product_reviews', '#judgeme'].includes(hash);
  const scrollToReviews = () => reviewRoot.scrollIntoView({block:'start', behavior:'auto'});
  const waitForApp = () => new Promise((resolve, reject) => {
    const started = Date.now();
    const check = () => {
      if (window.jdgm?.loadScript && window.jdgm.CDN_BASE_URL) return resolve(window.jdgm);
      if (Date.now() - started >= 10000) return reject(Error('Review service not ready'));
      setTimeout(check, 100);
    };
    check();
  });
  // Judge.me's current Vue entry scans once. A module load event alone does not
  // mean its asynchronous manager has mounted a usable review widget.
  const waitForWidget = widget => new Promise((resolve, reject) => {
    const mounted = () => widget.matches('[data-v-app]') || widget.querySelector('[data-v-app]');
    if (mounted()) return resolve();
    const observer = new MutationObserver(() => {
      if (mounted()) { clearTimeout(timer); observer.disconnect(); resolve(); }
    });
    const timer = setTimeout(() => {
      observer.disconnect(); reject(Error('Review widget did not initialize'));
    }, 15000);
    observer.observe(widget, {attributes:true, childList:true, subtree:true});
  });
  const allowedAsset = value => {
    const url = new URL(value, location.href);
    if (url.protocol !== 'https:' || ![location.hostname, 'cdn.shopify.com', 'cdnwidget.judge.me', 'cdn2.judge.me'].includes(url.hostname)) throw Error('Unexpected review asset');
    return url.href;
  };
  const loadStyle = href => {
    href = allowedAsset(href);
    if ([...document.querySelectorAll('link[rel="stylesheet"]')].some(node => node.href === href)) return;
    const node = document.createElement('link');
    node.rel = 'stylesheet'; node.href = href; document.head.append(node);
  };
  async function initWidgets(section) {
    // App-owned inline scripts carry the Shopify metafield data for this product.
    for (const old of section.querySelectorAll('script')) {
      if (old.type && !['text/javascript', 'application/javascript', 'module'].includes(old.type)) continue;
      const script = document.createElement('script');
      if (old.type) script.type = old.type;
      if (old.src) { script.src = allowedAsset(old.src); script.async = false; }
      else script.textContent = old.textContent;
      old.replaceWith(script);
    }
    const widgets = [...section.querySelectorAll('.jdgm-widget[data-entry-point][data-entry-key]')];
    if (!widgets.length) throw Error('Review widget unavailable');
    const app = await waitForApp();
    // The native write-review modal requires Judge.me's core, even with the new widget.
    // Load it here rather than during the product introduction.
    if (typeof app.$ !== 'function') {
      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(Error('Review core unavailable')), 10000);
        app.loadScript('widget/base.js', () => {
          app.loadScript('widget/common.js', () => { clearTimeout(timer); resolve(); });
        });
      });
      app.triggerVanillaEvent?.('finishLoadingCore');
    }
    app.loadCSS?.(app.widgetPath('base.css'));
    // Judge.me's core remains installed; load its own widget entry and CSS on demand.
    const base = window.jdgm?.CDN_BASE_URL;
    if (!base) throw Error('Review service not ready');
    let manifest = {};
    const manifestController = new AbortController();
    const manifestTimeout = setTimeout(() => manifestController.abort(), 4000);
    try {
      const response = await fetch(allowedAsset(base + 'manifest.json'), {signal:manifestController.signal});
      if (response.ok) manifest = await response.json();
    } catch (_) { /* The widget module also has its own dependency loader. */ }
    finally { clearTimeout(manifestTimeout); }
    const visited = new Set();
    function css(key) {
      if (visited.has(key)) return;
      visited.add(key);
      const item = manifest[key];
      item?.css?.forEach(file => loadStyle(base + file));
      item?.imports?.forEach(css);
    }
    for (const widget of widgets) {
      css(widget.dataset.entryKey);
      const src = allowedAsset(base + widget.dataset.entryPoint);
      if (![...document.scripts].some(node => node.src === src)) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.type = 'module'; script.src = src;
          const timer = setTimeout(() => { script.remove(); reject(Error('Review widget timed out')); }, 15000);
          script.onload = () => { clearTimeout(timer); resolve(); };
          script.onerror = () => { clearTimeout(timer); script.remove(); reject(Error('Review widget unavailable')); };
          document.head.append(script);
        });
      }
      await waitForWidget(widget);
    }
  }
  async function load(event) {
    event?.preventDefault();
    if (needsReload) {
      // Retrying a cached ES module on a replacement DOM node cannot reliably
      // re-run Judge.me's one-time scan. Let the visitor start a clean document.
      if (event) {
        const url = new URL(location.href);
        url.hash = 'customer-reviews';
        history.replaceState(null, '', url.href);
        location.reload();
      }
      return;
    }
    if (event) wantsScroll = true;
    if (ready) { if (wantsScroll) scrollToReviews(); return; }
    if (pending) return;
    pending = true;
    host.setAttribute('aria-busy', 'true');
    status.textContent = (host.dataset.loading || "Loading reviews…");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const url = new URL(link.dataset.sectionUrl || link.href, location.href);
      if (url.origin !== location.origin) throw Error('Invalid section URL');
      const response = await fetch(url, {signal:controller.signal, credentials:'same-origin'});
      if (!response.ok) throw Error('Review section unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.getElementById('shopify-section-' + host.dataset.sectionId);
      if (!section || section.querySelector('[data-ss-deferred-reviews]')) throw Error('Invalid review section');
      const node = document.importNode(section, true);
      const mount = host.closest('.shopify-section') || host;
      const oldId = mount.id;
      mount.removeAttribute('id');
      mount.after(node);
      try { await initWidgets(node); } catch (error) {
        needsReload = true;
        node.remove(); mount.id = oldId; throw error;
      }
      // Keep a real anchor before and after the asynchronous widget mount.
      host.removeAttribute('id');
      const anchor = document.createElement('div');
      anchor.id = 'customer-reviews';
      anchor.tabIndex = -1;
      anchor.style.scrollMarginTop = '120px';
      node.prepend(anchor);
      reviewRoot = anchor;
      ready = true;
      mount.remove();
      observer?.disconnect();
      if (wantsScroll) requestAnimationFrame(scrollToReviews);
    } catch (_) {
      host.setAttribute('aria-busy', 'false');
      status.textContent = needsReload ? (host.dataset.restartError || "The review service could not start. Reload this page to try again.") : (host.dataset.loadError || "Reviews could not load. Please try again.");
      link.textContent = needsReload ? (host.dataset.reloadLabel || "Reload page to view reviews") : (host.dataset.retryLabel || "Try loading reviews again");
    } finally { pending = false; clearTimeout(timeout); }
  }
  link.addEventListener('click', load);
  let observer;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) load();
    }, {rootMargin:'600px 0px'});
    observer.observe(host);
  }
  function openFromAnchor() {
    if (!reviewHash(location.hash)) return;
    wantsScroll = true;
    requestAnimationFrame(scrollToReviews);
    load();
  }
  window.addEventListener('hashchange', openFromAnchor);
  // Clicking the same hash again does not emit hashchange.
  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || !reviewHash(url.hash)) return;
    wantsScroll = true;
    requestAnimationFrame(scrollToReviews);
    load();
  });
  if (reviewHash(location.hash)) openFromAnchor();
  else if (/judgeme|jdgm/i.test(location.hash + location.search)) load();
})();
